// pages/tools/ROASCalculator.jsx
import { useState } from 'react';

export default function ROASCalculator() {
  const [currency, setCurrency] = useState('INR');
  const [adSpend, setAdSpend] = useState('');
  const [revenue, setRevenue] = useState('');
  const [cogs, setCogs] = useState('');
  const [results, setResults] = useState(null);

  const currencySymbols = {
    INR: '₹',
    USD: '₹',
    EUR: '€',
    GBP: '£',
    AED: 'د.إ'
  };

  const calculateROAS = () => {
    if (!adSpend || !revenue) {
      alert('Please fill in Ad Spend and Revenue fields');
      return;
    }

    const spend = parseFloat(adSpend);
    const rev = parseFloat(revenue);
    const cost = parseFloat(cogs) || 0;

    // Basic calculations
    const roasValue = (rev / spend).toFixed(2);
    const grossProfit = rev - cost;
    const netProfit = grossProfit - spend;
    const profitMargin = ((netProfit / rev) * 100).toFixed(2);
    const roi = ((netProfit / spend) * 100).toFixed(2);
    const breakEvenRoas = cost > 0 ? ((spend + cost) / spend).toFixed(2) : '1.00';

    // Performance metrics
    const cpa = rev > 0 ? (spend / (rev / 1000)).toFixed(2) : '0'; // Assuming avg order value
    const conversionValue = (rev / spend).toFixed(2);

    setResults({
      roas: roasValue,
      grossProfit: grossProfit.toFixed(2),
      netProfit: netProfit.toFixed(2),
      profitMargin,
      roi,
      breakEvenRoas,
      cpa,
      conversionValue,
      totalExpenses: (spend + cost).toFixed(2)
    });
  };

  const resetCalculator = () => {
    setAdSpend('');
    setRevenue('');
    setCogs('');
    setResults(null);
  };

  const getPerformanceLevel = (roas) => {
    const roasNum = parseFloat(roas);
    if (roasNum >= 5) return { level: 'Exceptional', color: 'text-green-400', bgColor: 'bg-green-400/20', borderColor: 'border-green-400/30' };
    if (roasNum >= 4) return { level: 'Excellent', color: 'text-green-400', bgColor: 'bg-green-400/20', borderColor: 'border-green-400/30' };
    if (roasNum >= 3) return { level: 'Very Good', color: 'text-blue-400', bgColor: 'bg-blue-400/20', borderColor: 'border-blue-400/30' };
    if (roasNum >= 2) return { level: 'Good', color: 'text-yellow-400', bgColor: 'bg-yellow-400/20', borderColor: 'border-yellow-400/30' };
    if (roasNum >= 1) return { level: 'Fair', color: 'text-orange-400', bgColor: 'bg-orange-400/20', borderColor: 'border-orange-400/30' };
    return { level: 'Needs Improvement', color: 'text-red-400', bgColor: 'bg-red-400/20', borderColor: 'border-red-400/30' };
  };

  const formatNumber = (num) => {
    if (currency === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      }).format(num);
    }
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            Advanced ROAS Calculator
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Calculate your Return on Ad Spend with detailed profitability analysis and performance insights
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-[#00809D] to-[#00a0c4] rounded-full"></span>
              Enter Your Data
            </h2>

            {/* Currency Selector */}
            <div className="mb-6">
              <label className="block text-white font-semibold mb-3 text-sm sm:text-base">
                Select Currency
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {['INR', 'USD', 'EUR', 'GBP', 'AED'].map((curr) => (
                  <button
                    key={curr}
                    onClick={() => setCurrency(curr)}
                    className={`py-2 px-3 sm:px-4 rounded-xl font-semibold transition-all text-sm sm:text-base ${currency === curr
                      ? 'bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white shadow-lg shadow-[#00809D]/50'
                      : 'bg-gray-800 text-gray-400 border border-white/10 hover:border-[#00809D]/50'
                      }`}
                  >
                    {curr}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              {/* Ad Spend */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Total Ad Spend
                  <span className="text-red-400">*</span>
                  <span className="text-xs text-gray-500">(Required)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    {currencySymbols[currency]}
                  </span>
                  <input
                    type="number"
                    value={adSpend}
                    onChange={(e) => setAdSpend(e.target.value)}
                    placeholder="50,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Revenue */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Revenue Generated
                  <span className="text-red-400">*</span>
                  <span className="text-xs text-gray-500">(Required)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    {currencySymbols[currency]}
                  </span>
                  <input
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    placeholder="2,00,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* COGS */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Cost of Goods Sold (COGS)
                  <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    {currencySymbols[currency]}
                  </span>
                  <input
                    type="number"
                    value={cogs}
                    onChange={(e) => setCogs(e.target.value)}
                    placeholder="75,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Include product costs, shipping, fulfillment</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8">
              <button
                onClick={calculateROAS}
                className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 text-base sm:text-lg"
              >
                Calculate ROAS
              </button>
              <button
                onClick={resetCalculator}
                className="sm:w-auto bg-gray-800 text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-[#00809D] to-[#00a0c4] rounded-full"></span>
              Results & Analysis
            </h2>

            {results ? (
              <div className="space-y-5">
                {/* ROAS Card */}
                <div className={`${getPerformanceLevel(results.roas).bgColor} border ${getPerformanceLevel(results.roas).borderColor} rounded-2xl p-6 text-center`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <p className="text-gray-400 text-sm sm:text-base">Your ROAS is</p>
                    <span className={`px-3 py-1 ${getPerformanceLevel(results.roas).bgColor} ${getPerformanceLevel(results.roas).color} rounded-full text-xs font-bold`}>
                      {getPerformanceLevel(results.roas).level}
                    </span>
                  </div>
                  <p className={`text-5xl sm:text-6xl lg:text-7xl font-bold ${getPerformanceLevel(results.roas).color} mb-2`}>
                    {results.roas}<span className="text-2xl sm:text-3xl">:1</span>
                  </p>
                  <p className="text-gray-300 text-sm sm:text-base">
                    For every {currencySymbols[currency]}1 spent, you earned {currencySymbols[currency]}{results.roas}
                  </p>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Net Profit</p>
                    <p className={`text-xl sm:text-2xl lg:text-3xl font-bold ${parseFloat(results.netProfit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {currencySymbols[currency]}{formatNumber(Math.abs(results.netProfit))}
                    </p>
                  </div>
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">ROI</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#00809D]">
                      {results.roi}%
                    </p>
                  </div>
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Profit Margin</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-400">
                      {results.profitMargin}%
                    </p>
                  </div>
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Gross Profit</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-purple-400">
                      {currencySymbols[currency]}{formatNumber(results.grossProfit)}
                    </p>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="space-y-3">
                  <div className="bg-gray-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-gray-400 text-sm sm:text-base">Break-Even ROAS</span>
                    <span className="text-white font-bold text-base sm:text-lg">{results.breakEvenRoas}:1</span>
                  </div>
                  <div className="bg-gray-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-gray-400 text-sm sm:text-base">Total Expenses</span>
                    <span className="text-white font-bold text-base sm:text-lg">{currencySymbols[currency]}{formatNumber(results.totalExpenses)}</span>
                  </div>
                  <div className="bg-gray-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-gray-400 text-sm sm:text-base">Conversion Value</span>
                    <span className="text-white font-bold text-base sm:text-lg">{currencySymbols[currency]}{results.conversionValue}</span>
                  </div>
                </div>

                {/* Performance Analysis */}
                <div className="bg-gradient-to-br from-[#00809D]/10 to-[#00a0c4]/10 border border-[#00809D]/30 rounded-2xl p-5">
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <svg className="w-5 h-5 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    AI-Powered Insights
                  </h4>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    {parseFloat(results.roas) >= 5 ?
                      "🎉 Outstanding performance! Your campaigns are generating exceptional returns. Focus on scaling while maintaining quality. Consider increasing budget for top-performing campaigns." :
                      parseFloat(results.roas) >= 4 ?
                        "✅ Excellent ROAS! You're well above industry benchmarks. Continue optimizing your best-performing ads and audiences to maintain this performance." :
                        parseFloat(results.roas) >= 3 ?
                          "👍 Very good results! Your campaigns are profitable. Test new audiences and ad variations to push towards 4:1 ROAS." :
                          parseFloat(results.roas) >= 2 ?
                            "⚡ Decent performance, but there's room for improvement. Focus on improving ad quality, targeting precision, and conversion rate optimization." :
                            parseFloat(results.roas) >= 1 ?
                              "⚠️ Fair results - breaking even. Urgently optimize your campaigns: refine targeting, improve ad creatives, and reduce cost per click." :
                              "🚨 Critical: Your campaigns are losing money. Pause low-performing ads immediately. Review targeting, landing pages, and offer competitiveness. Consider strategy overhaul."
                    }
                  </p>
                </div>

                {/* Action Recommendations */}
                <div className="bg-gray-900/50 border border-white/10 rounded-xl p-5">
                  <h4 className="text-white font-bold mb-3 text-sm sm:text-base">Recommended Actions</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-400">
                    {parseFloat(results.roas) >= 4 ? (
                      <>
                        <li className="flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>Scale budget by 20-30% for top campaigns</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>Expand to lookalike audiences</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>Test new ad formats and placements</span>
                        </li>
                      </>
                    ) : parseFloat(results.roas) >= 2 ? (
                      <>
                        <li className="flex items-start gap-2">
                          <span className="text-yellow-400 mt-0.5">→</span>
                          <span>A/B test ad creatives and copy</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-yellow-400 mt-0.5">→</span>
                          <span>Refine audience targeting parameters</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-yellow-400 mt-0.5">→</span>
                          <span>Optimize landing page conversion rate</span>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">!</span>
                          <span>Pause underperforming campaigns immediately</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">!</span>
                          <span>Review and improve targeting strategy</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">!</span>
                          <span>Redesign landing pages and offers</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#00809D]/20 to-[#00a0c4]/20 rounded-2xl flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-400 text-base sm:text-lg mb-2">Enter your data to calculate ROAS</p>
                <p className="text-gray-600 text-xs sm:text-sm">Fill in the required fields and click Calculate</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Understanding ROAS</h3>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm sm:text-base">
              Return on Ad Spend (ROAS) is a crucial marketing metric that measures the revenue generated for every rupee spent on advertising.
              It helps you understand campaign profitability and make data-driven budget decisions.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#00809D]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00809D] font-bold">1</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">Formula</p>
                  <p className="text-gray-400 text-xs sm:text-sm">ROAS = Revenue Generated ÷ Ad Spend</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#00809D]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00809D] font-bold">2</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">Interpretation</p>
                  <p className="text-gray-400 text-xs sm:text-sm">A 4:1 ROAS means ₹4 revenue for every ₹1 spent</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#00809D]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00809D] font-bold">3</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">Break-Even</p>
                  <p className="text-gray-400 text-xs sm:text-sm">Include COGS to calculate true break-even point</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Industry Benchmarks</h3>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm sm:text-base">
              ROAS varies by industry, business model, and campaign objectives. Here are general benchmarks:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-black/50 border border-white/10 rounded-xl p-4">
                <p className="text-red-400 font-bold mb-1 text-sm sm:text-base">Below 1:1</p>
                <p className="text-gray-400 text-xs sm:text-sm">Losing money - urgent action needed</p>
              </div>
              <div className="bg-black/50 border border-white/10 rounded-xl p-4">
                <p className="text-orange-400 font-bold mb-1 text-sm sm:text-base">1:1 to 2:1</p>
                <p className="text-gray-400 text-xs sm:text-sm">Break-even to fair performance</p>
              </div>
              <div className="bg-black/50 border border-white/10 rounded-xl p-4">
                <p className="text-yellow-400 font-bold mb-1 text-sm sm:text-base">2:1 to 4:1</p>
                <p className="text-gray-400 text-xs sm:text-sm">Good - industry standard</p>
              </div>
              <div className="bg-black/50 border border-white/10 rounded-xl p-4">
                <p className="text-green-400 font-bold mb-1 text-sm sm:text-base">Above 4:1</p>
                <p className="text-gray-400 text-xs sm:text-sm">Excellent - highly profitable</p>
              </div>
            </div>
            <div className="mt-6 bg-[#00809D]/10 border border-[#00809D]/30 rounded-xl p-4">
              <p className="text-[#00809D] font-semibold mb-2 text-xs sm:text-sm">💡 Pro Tip</p>
              <p className="text-gray-400 text-xs sm:text-sm">E-commerce typically targets 4:1+ ROAS, while lead generation campaigns may be profitable at 2-3:1 depending on customer lifetime value.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
